<script setup lang="ts">
import useDashboard from '@/modules/dashboard/composables/useDashboard';
import { Squares2X2Icon } from '@heroicons/vue/24/outline';
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/16/solid';
import ButtonG from '@/modules/shared/components/ButtonComponent.vue';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { ref, onMounted } from 'vue';

const { dashboard, href } = useDashboard();
const dashboardContainer = ref();

const contact = () => {
    window.open('mailto:evalocci@emailmeter.com', '_blank');
};

onMounted(() => {
    if (dashboard.value.dashboard_platform === 'Superset') {
        embedDashboard({
            id: dashboard.value.dashboard_uuid,
            supersetDomain: import.meta.env.VITE_SUPERSET_DOMAIN,
            mountPoint: dashboardContainer.value,
            fetchGuestToken: () => Promise.resolve(dashboard.value.jwt_token),
            dashboardUiConfig: {
                // Add any minimal UI config you need, e.g.:
                // hideTitle: true,
                hideTitle: false,
                hideChartControls: false
            },
            iframeSandboxExtras: ['allow-top-navigation'],

            debug: true // Keep debug true for troubleshooting
        }).catch((error) => {
            console.error('Superset SDK embedding error:', error);
            dashboardContainer.value.innerHTML = '<p>Error embedding Superset dashboard. Check console.</p>';
        });
    }
});
</script>

<template>
    <div class="flex flex-col w-full h-full" :class="dashboard.dashboard_platform === 'Looker Studio' ? 'max-w-[1120px]' : 'pt-10 max-w-[1322px]'">
        <div v-if="dashboard.dashboard_platform === 'Looker Studio'" class="flex justify-between w-full items-start">
            <div class="flex flex-col gap-1 justify-start">
                <p class="text-lg font-semibold text-gray-950">Your dashboard</p>
                <p class="text-gray-500 text-sm font-normal">Measure your team’s email activity and track KPIs</p>
            </div>

            <ButtonG class="my-auto" text="Open in Looker Studio" hierarcy="outline" :icon-trailing="ArrowTopRightOnSquareIcon" @click="href" />
        </div>
        <div class="h-full rounded-t-lg border border-solid border-gray-200 bg-white shadow-default" :class="dashboard.dashboard_platform === 'Looker Studio' ? ' mt-5' : ''">
            <iframe
                v-if="dashboard.dashboard_platform === 'Looker Studio'"
                :src="dashboard.dashboard_embedded_url"
                class="h-full shadow rounded-t-lg"
                style="border: none"
                height="92%"
                width="100%"
                frameborder="0"
            />
            <div ref="dashboardContainer" id="dashboardContainer" v-else-if="dashboard.dashboard_platform === 'Superset'" class="h-full w-full shadow rounded-t-lg"></div>
            <div v-else class="flex h-full flex-col justify-center items-center">
                <div class="p-3.5 bg-indigo-100 rounded-full">
                    <Squares2X2Icon class="w-6 h-6 text-indigo-600" />
                </div>
                <p class="text-gray-900 text-base leading-6 font-medium pt-2">Your dashboard will be ready soon</p>
                <p class="text-gray-500 text-sm leading-5 w-2/5 text-center pt-1">
                    We’re loading your data and getting your metrics ready for you! If you have any questions or want an update, please get in touch.
                </p>
                <div class="p-6 flex justify-center">
                    <ButtonG size="lg" text="Contact your Account Manager" @click="contact" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="css">
#dashboardContainer iframe {
    border: 0;
    height: 100%;
    width: 100%;
    border-radius: 0.5rem;
}
</style>
